import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "../../common/reduxQuery/helpers";
import {
  CandidateBatch,
  CandidateDetail,
  QualityLabPageArgs,
  QualityLabPage,
  LookupPage,
  Membership,
  SetKey,
  SetRemovedArgs,
} from "../interfaces/qualityLab";
import {
  EditorModel,
  NormalizeRequestValue,
  NormalizeResult,
  SaveAnnotationsBody,
} from "../../documentEditor/interfaces/editor";
import { AddToSetResult, SearchFacet } from "../interfaces/qualityLab";

const query = (
  entries: Record<string, string | number | boolean | undefined>,
) => {
  const params = new URLSearchParams();
  Object.entries(entries).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      params.set(key, String(value));
    }
  });
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

export const qualityLabApi = createApi({
  reducerPath: "qualityLabApi",
  baseQuery: customBaseQuery(),
  tagTypes: ["Store", "Candidates", "Lookup", "Membership", "Editor"],
  endpoints: (build) => ({
    getStore: build.query<QualityLabPage, QualityLabPageArgs>({
      query: ({
        set,
        search,
        formType,
        state,
        source,
        removed,
        sort,
        page,
        size,
      }) => ({
        url: `/api/v1/quality-lab/sets/${set}${query({
          search,
          formType,
          state,
          source,
          removed,
          sort,
          page,
          size,
        })}`,
        method: "GET",
      }),
      providesTags: ["Store"],
    }),
    getCandidates: build.query<CandidateBatch, string | void>({
      query: (seed) => ({
        url: `/api/v1/quality-lab/candidates${query({ seed: seed || undefined })}`,
        method: "GET",
      }),
      providesTags: ["Candidates"],
    }),
    getCandidate: build.query<CandidateDetail, string>({
      query: (id) => ({
        url: `/api/v1/quality-lab/candidates/${id}`,
        method: "GET",
      }),
      providesTags: ["Candidates"],
    }),
    verifyCandidate: build.mutation<void, string>({
      query: (id) => ({
        url: `/api/v1/quality-lab/candidates/${id}/verify`,
        method: "POST",
      }),
      invalidatesTags: ["Candidates", "Store"],
    }),
    /**
     * The search keys available to this user. Configured per setup, so it cannot be a constant in
     * the client: one customer's categorizations are not another's.
     */
    getFacets: build.query<Array<SearchFacet>, { inStore: boolean }>({
      query: ({ inStore }) => ({
        url: `/api/v1/quality-lab/facets${query({ inStore: String(inStore) })}`,
        method: "GET",
      }),
    }),

    getLookup: build.query<
      LookupPage,
      { search?: string; page?: number; size?: number }
    >({
      query: ({ search, page, size }) => ({
        url: `/api/v1/quality-lab/lookup${query({ search, page, size })}`,
        method: "GET",
      }),
      providesTags: ["Lookup"],
    }),
    addToSet: build.mutation<
      AddToSetResult,
      { set: SetKey; documentSetIds: Array<string> }
    >({
      query: ({ set, documentSetIds }) => ({
        url: `/api/v1/quality-lab/sets/${set}`,
        method: "POST",
        body: { documentSetIds },
      }),
      invalidatesTags: ["Store", "Lookup", "Candidates", "Membership"],
    }),

    /**
     * Takes documents out of a store, or puts them back. One mutation with the
     * target as an argument rather than two, because they are the same edit and
     * restoring is only setting it back to false.
     *
     * Invalidates Candidates as well as Store: removing a document takes it off
     * the verification worklist, and restoring one puts it back.
     */
    setRemoved: build.mutation<AddToSetResult, SetRemovedArgs>({
      query: ({ documentSetIds, removed }) => ({
        url: `/api/v1/quality-lab/documents/removed`,
        method: "PUT",
        body: { documentSetIds, removed },
      }),
      invalidatesTags: ["Store", "Lookup", "Candidates", "Membership"],
    }),

    /**
     * The shape and values of a document. Without a version those are the values
     * as they stand; with one they are the values that version was closed with,
     * which is how the screen shows the labels the document was exported with.
     */
    getEditorModel: build.query<
      EditorModel,
      { documentSetId: string; version?: number }
    >({
      query: ({ documentSetId, version }) => ({
        url: `/api/v1/quality-lab/documents/${documentSetId}/editor${
          version === undefined ? "" : `?version=${version}`
        }`,
        method: "GET",
      }),
      providesTags: (r, e, arg) => [
        { type: "Editor" as const, id: arg.documentSetId },
      ],
    }),

    /**
     * A preview of what corrected values become after their normalizations.
     * Invalidates nothing: nothing is saved, and the save normalizes again. The
     * categorizations on screen go along, because a number reads its decimal
     * separator from one, and a date its region.
     */
    normalizeValues: build.mutation<
      Array<NormalizeResult>,
      {
        documentSetId: string;
        values: Array<NormalizeRequestValue>;
        categorizations: Array<{
          categorizationId: string;
          index: number;
          externalDataId: string;
        }>;
      }
    >({
      query: ({ documentSetId, values, categorizations }) => ({
        url: `/api/v1/quality-lab/documents/${documentSetId}/normalize`,
        method: "POST",
        body: { values, categorizations },
      }),
    }),

    /**
     * The same preview for the normalizations that run on the customer service.
     * That service reads the whole document, so the request carries every value
     * and the master data record each categorization points at.
     */
    normalizeCustomerValues: build.mutation<
      Array<NormalizeResult>,
      {
        documentSetId: string;
        values: Array<NormalizeRequestValue>;
        categorizations: Array<{
          categorizationId: string;
          index: number;
          externalDataId: string;
        }>;
      }
    >({
      query: ({ documentSetId, values, categorizations }) => ({
        url: `/api/v1/quality-lab/documents/${documentSetId}/normalize-customer`,
        method: "POST",
        body: { values, categorizations },
      }),
    }),

    saveAnnotations: build.mutation<
      void,
      { documentSetId: string; body: SaveAnnotationsBody }
    >({
      query: ({ documentSetId, body }) => ({
        url: `/api/v1/quality-lab/documents/${documentSetId}/annotations`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (r, e, { documentSetId }) => [
        { type: "Editor" as const, id: documentSetId },
        "Candidates",
        "Store",
      ],
    }),

    getMembership: build.query<Membership, string>({
      query: (documentSetId) => ({
        url: `/api/v1/quality-lab/membership/${documentSetId}`,
        method: "GET",
      }),
      // Tagged per document set: adding one document must not refetch the
      // membership of every other document the session has looked at.
      providesTags: (result, error, documentSetId) => [
        { type: "Membership" as const, id: documentSetId },
      ],
    }),
  }),
});

export const {
  useGetStoreQuery,
  useGetFacetsQuery,
  useGetCandidatesQuery,
  useGetCandidateQuery,
  useVerifyCandidateMutation,
  useGetLookupQuery,
  useAddToSetMutation,
  useSetRemovedMutation,
  useGetMembershipQuery,
  useGetEditorModelQuery,
  useNormalizeValuesMutation,
  useNormalizeCustomerValuesMutation,
  useSaveAnnotationsMutation,
} = qualityLabApi;
